import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Button, Container, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import http from '../../../utils/axios';
import numeral from 'numeral';
import { Tooltip } from '@mui/material';
import usePalette from '../../../hooks/usePalette';
import LoadingState from '../../../components/common/LoadingState';

// Icons
import Icon from '@mdi/react';
import { mdiMicrosoftExcel, mdiFilterVariant, mdiMagnify, mdiClose } from '@mdi/js';

// components
import AnalysisTable from './components/AnalysisTable';
import MinistryFilter from './components/MinistryFilter';

// redux
import { fetchProjects } from '../../../redux/slices/projects/analysisTableSlice';
import { getRmkItems, getMinistries } from '../../../redux/slices/utilsSlice';

import { ModuleRegistry } from '@ag-grid-community/core';
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export';
import MoreSearchField from './components/MoreSearchField';

ModuleRegistry.registerModules([ExcelExportModule]);

// Must match the flag in AnalysisTable.js. Stage 1 server-side rollout —
// when true, AG Grid fetches rows directly via a datasource and the legacy
// up-front fetch + Redux store of rowData is bypassed. Excel export is
// disabled until Stage 3 wires the multi-page fetch.
const USE_SERVER_SIDE = true;

const MainPage = () => {
  const gridRef = useRef(null);
  const dispatch = useDispatch();
  const palette = usePalette();
  const abortControllerRef = useRef(null);

  const [selectRmk, setSelectRmk] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [showCanvas, setShowCanvas] = useState(false);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  // Project-name search box above the table. It drives the NAMA PROJEK
  // column's own text filter, so the server-side query, the export and the
  // grid's filter state all stay in sync.
  const [nameSearch, setNameSearch] = useState('');
  const nameSearchTimer = useRef(null);
  const [scopeFilter, setScopeFilter] = useState({ departments: [], jkr_branches: [] });

  const { currentRmk, rmkOptions, ministries } = useSelector((state) => ({
    currentRmk: state.rmkReducer.activeRmk,
    rmkOptions: state.utilsReducer.dropdownItems.rmkItems,
    ministries: state.utilsReducer.dropdownItems.ministries,
  }));

  // Memoize column state from localStorage
  const columnState = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('columnState'));
    } catch {
      return null;
    }
  }, []);

  // Fetch projects with abort controller for cleanup
  const fetchProjectsData = useCallback(
    async (rmk) => {
      if (!rmk?.number) return;
      // Server-side mode: AG Grid datasource owns the fetching now.
      if (USE_SERVER_SIDE) {
        setLoading(false);
        return;
      }

      // Cancel previous request if exists
      if (abortControllerRef.current) {
        abortControllerRef.current.cancel('New request initiated');
      }

      abortControllerRef.current = axios.CancelToken.source();
      setLoading(true);

      try {
        const props = {
          params: {
            rmk_id: rmk.number,
            is_federal_projects:
              JSON.parse(localStorage.getItem('federalProjects'))?.value || false,
          },
          cancelToken: abortControllerRef.current.token,
        };

        const result = await dispatch(fetchProjects(props)).unwrap();
        setRowData(result || []);
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error('Error fetching projects:', error);
          setRowData([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [dispatch]
  );

  const handleSearchMoreField = useCallback(() => {
    if (USE_SERVER_SIDE) {
      // Force the grid to refetch with the new RMK / federal flag.
      gridRef.current?.api?.refreshServerSide?.({ purge: true });
      return;
    }
    fetchProjectsData(selectRmk);
  }, [fetchProjectsData, selectRmk]);

  // Walk a dot-separated field path on a row, e.g. "ministry.short_form".
  const readField = (data, path) =>
    path?.split('.').reduce((o, k) => (o == null ? o : o[k]), data);

  // Fetch ALL leaves matching the current filter+sort. Used by export in SS mode.
  const fetchAllRowsForExport = useCallback(async () => {
    const api = gridRef.current?.api;
    if (!api) return [];
    const filterModel = api.getFilterModel?.() ?? {};
    const sortModel =
      api.getSortModel?.() ??
      (gridRef.current?.columnApi?.getColumnState?.() ?? [])
        .filter((s) => s.sort)
        .map((s) => ({ colId: s.colId, sort: s.sort }));

    const isFederalProjects =
      JSON.parse(localStorage.getItem('federalProjects'))?.value || false;
    const PAGE = 500;
    const all = [];
    let startRow = 0;
    let lastRow = Infinity;
    while (startRow < lastRow) {
      const { data } = await http.post('/project-analysis-table/get-server-side', {
        rmk_id: selectRmk?.number,
        is_federal_projects: isFederalProjects,
        startRow,
        endRow: startRow + PAGE,
        sortModel,
        filterModel,
        scope_filter: scopeFilter ?? {},
      });
      const payload = data?.data ?? data;
      const batch = payload?.rows ?? [];
      lastRow = payload?.lastRow ?? batch.length;
      all.push(...batch);
      if (batch.length === 0) break;
      startRow += batch.length;
    }
    return all;
  }, [selectRmk, scopeFilter]);

  // Download Excel — aoa built on main thread, then handed off to a Web
  // Worker so ExcelJS serialisation never blocks the UI. In server-side
  // mode we paginate-fetch every matching leaf first.
  const onBtnExport = useCallback(async () => {
    if (!gridRef.current?.api || !gridRef.current?.columnApi) return;

    setExporting(true);
    // Yield once so React can render the spinner before we start
    await new Promise((resolve) => setTimeout(resolve, 0));

    try {
      const displayedColumns = gridRef.current.columnApi.getAllDisplayedColumns();

      // Build an array of plain row data + a parallel "node-ish" object that the
      // existing AG Grid valueGetters can use (they only read params.data).
      let rowsData;
      if (USE_SERVER_SIDE) {
        rowsData = await fetchAllRowsForExport();
      } else {
        const rowNodes = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
          if (!node.group) rowNodes.push(node.data);
        });
        rowsData = rowNodes;
      }

      // Evaluate a column for one row without relying on AG Grid's
      // gridApi.getValue — works on plain data objects.
      const getCellValue = (col, data, exportRowIndex) => {
        const def = col.getColDef();
        const headerName = def.headerName;

        if (headerName === '#') return exportRowIndex;

        if (headerName === 'DAERAH') return data?.site_information?.daerah?.name || '';
        if (headerName === 'PARLIMEN') {
          const p = data?.site_information?.parliament;
          return p ? `${p.name} - ${p.code}` : '';
        }
        if (headerName === 'DUN') {
          const dun = data?.site_information?.state_legislative_assembly;
          return dun ? `${dun.name} - ${dun.code}` : '';
        }

        // Function valueGetters: invoke with a synthetic params object.
        if (typeof def.valueGetter === 'function') {
          try {
            return (
              def.valueGetter({
                data,
                node: { rowIndex: exportRowIndex - 1, data },
                colDef: def,
              }) ?? ''
            );
          } catch {
            return '';
          }
        }

        if (def.field) return readField(data, def.field) ?? '';
        return '';
      };

      const headers = [];
      // Parallel flags so the worker knows which columns are numeric and
      // can apply a thousands-separator number format in Excel.
      const colIsNumeric = [];
      displayedColumns.forEach((col) => {
        const def = col.getColDef();
        const isNumeric = def.cellDataType === 'number';
        headers.push(def.headerName || '');
        colIsNumeric.push(isNumeric);
        if (def.headerName === 'SKOP PROJEK ASAL') {
          headers.push('SILING SKOP (RM)');
          colIsNumeric.push(true); // ceiling sub-column
        }
      });

      const aoa = [headers];
      const merges = [];

      // Build aoa in chunks of 100 rows to stay responsive
      const CHUNK = 100;
      for (let i = 0; i < rowsData.length; i++) {
        const data = rowsData[i];
        const scopeItems = data?.financial_details || [];
        const rowCount = Math.max(scopeItems.length, 1);
        const startRow = aoa.length;

        for (let scopeIdx = 0; scopeIdx < rowCount; scopeIdx++) {
          const row = [];
          displayedColumns.forEach((col) => {
            const headerName = col.getColDef().headerName;
            if (headerName === 'SKOP PROJEK ASAL') {
              const item = scopeItems[scopeIdx];
              row.push(item?.cost_description || '');
              // Push raw number so Excel treats it as numeric and applies the
              // currency-style format in the worker.
              row.push(item?.ceiling != null ? Number(item.ceiling) : '');
            } else {
              row.push(scopeIdx > 0 ? '' : getCellValue(col, data, i + 1));
            }
          });
          aoa.push(row);
        }

        if (rowCount > 1) {
          let exportColIdx = 0;
          displayedColumns.forEach((col) => {
            if (col.getColDef().headerName === 'SKOP PROJEK ASAL') {
              exportColIdx += 2;
            } else {
              merges.push({
                s: { r: startRow, c: exportColIdx },
                e: { r: startRow + rowCount - 1, c: exportColIdx },
              });
              exportColIdx++;
            }
          });
        }

        if ((i + 1) % CHUNK === 0) {
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
      }

      // Hand the plain data to a worker — ExcelJS serialisation runs off the main thread.
      // The worker is a static asset in /public to avoid webpack loader conflicts.
      const workerUrl = process.env.PUBLIC_URL + '/export-worker.js';
      const buffer = await new Promise((resolve, reject) => {
        const worker = new Worker(workerUrl);
        worker.onmessage = ({ data: { success, buffer, error } }) => {
          worker.terminate();
          if (success) resolve(buffer);
          else reject(new Error(error));
        };
        worker.onerror = (e) => {
          worker.terminate();
          reject(e);
        };
        worker.postMessage({ aoa, merges, colIsNumeric });
      });

      const rmkLabel = selectRmk?.label ? ` - ${selectRmk.label}` : '';
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Jadual Analisis Dalaman${rmkLabel}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }, [selectRmk, fetchAllRowsForExport]);

  // Save columns state - debounced
  const saveColumnState = useCallback(() => {
    if (!gridRef.current?.columnApi) return;

    const columnState = gridRef.current.columnApi.getColumnState();
    localStorage.setItem('columnState', JSON.stringify(columnState));
  }, []);

  // Grid ready handler
  const applyNameFilter = useCallback((value) => {
    const api = gridRef.current?.api;
    if (!api) return;
    const model = { ...(api.getFilterModel?.() ?? {}) };
    const term = value.trim();
    if (term) {
      model.name = { filterType: 'text', type: 'contains', filter: term };
    } else {
      delete model.name;
    }
    api.setFilterModel(model);
  }, []);

  // Debounced so each keystroke does not fire a server request.
  const handleNameSearchChange = useCallback(
    (value) => {
      setNameSearch(value);
      clearTimeout(nameSearchTimer.current);
      nameSearchTimer.current = setTimeout(() => applyNameFilter(value), 400);
    },
    [applyNameFilter]
  );

  const clearNameSearch = useCallback(() => {
    clearTimeout(nameSearchTimer.current);
    setNameSearch('');
    applyNameFilter('');
  }, [applyNameFilter]);

  useEffect(() => () => clearTimeout(nameSearchTimer.current), []);

  const onGridReady = useCallback(
    (params) => {
      // Restore column state if available
      if (columnState && params?.columnApi) {
        params.columnApi.applyColumnState({
          state: columnState,
          applyOrder: true,
        });
      }
      // A remounted grid starts with an empty filter model — re-apply any
      // name search still shown in the box above the table.
      if (nameSearch.trim()) {
        applyNameFilter(nameSearch);
      }
    },
    [columnState, nameSearch, applyNameFilter]
  );

  // Apply the top-of-table scope filter (ministry/department/JKR branch).
  // The grid datasource reads `scopeFilter`; purge so cached blocks refetch.
  const handleChangeScopeFilter = useCallback((value) => {
    setScopeFilter(value);
  }, []);

  useEffect(() => {
    if (USE_SERVER_SIDE) {
      gridRef.current?.api?.refreshServerSide?.({ purge: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeFilter]);

  // Fetch RMK items + ministry hierarchy on mount
  useEffect(() => {
    const source = axios.CancelToken.source();
    dispatch(getRmkItems({ source }));
    dispatch(getMinistries({ source }));

    return () => {
      source.cancel('Component unmounted');
      if (abortControllerRef.current) {
        abortControllerRef.current.cancel('Component unmounted');
      }
    };
  }, [dispatch]);

  // Set initial selectRmk from currentRmk (only on first load)
  useEffect(() => {
    if (currentRmk && !selectRmk) {
      setSelectRmk(currentRmk);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRmk]);

  // Fetch projects when selectRmk changes
  useEffect(() => {
    if (selectRmk) {
      fetchProjectsData(selectRmk);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectRmk]);

  // Memoize handlers for canvas
  const handleOpenCanvas = useCallback(() => setShowCanvas(true), []);
  const handleCloseCanvas = useCallback(() => setShowCanvas(false), []);

  // Handle RMK change from MoreSearchField
  const handleChangeRmk = useCallback((value) => {
    setSelectRmk(value);
    console.log(value);
  }, []);

  return (
    <React.Fragment>
      <MoreSearchField
        showCanvas={showCanvas}
        closeCanvas={handleCloseCanvas}
        searchMoreField={handleSearchMoreField}
        selectRmk={selectRmk}
        onChangeRmk={handleChangeRmk}
      />

      <Container fluid className="p-0">
        <div className="d-flex justify-content-between mb-3">
          <div className="d-flex gap-2">
            <Tooltip title="Lebih Pilihan" placement="top" arrow>
              <Button variant="secondary" onClick={handleOpenCanvas}>
                <Icon path={mdiFilterVariant} size={0.8} />
              </Button>
            </Tooltip>
            <MinistryFilter ministries={ministries} onChange={handleChangeScopeFilter} />
            <div className="position-relative" style={{ width: 340 }}>
              <Icon
                path={mdiMagnify}
                size={0.8}
                className="position-absolute top-50 translate-middle-y text-muted"
                style={{ left: 10, pointerEvents: 'none' }}
              />
              <Form.Control
                type="text"
                placeholder="Cari nama projek..."
                value={nameSearch}
                onChange={(e) => handleNameSearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    clearTimeout(nameSearchTimer.current);
                    applyNameFilter(nameSearch);
                  }
                }}
                disabled={loading}
                // Same height as the Kementerian / Jabatan filter button.
                style={{ height: 34, paddingLeft: 36, paddingRight: 32 }}
              />
              {nameSearch && (
                <span
                  role="button"
                  title="Kosongkan"
                  onClick={clearNameSearch}
                  className="position-absolute top-50 translate-middle-y d-inline-flex text-muted"
                  style={{ right: 10, cursor: 'pointer' }}
                >
                  <Icon path={mdiClose} size={0.7} />
                </span>
              )}
            </div>
          </div>
          <div>
            <Tooltip title={exporting ? 'Sedang mengeksport...' : 'Muat Turun XLSX'} arrow>
              <Button onClick={onBtnExport} variant="light" disabled={loading || exporting}>
                {exporting ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    style={{ color: palette.success }}
                  />
                ) : (
                  <Icon path={mdiMicrosoftExcel} size={1} color={palette.success} />
                )}
              </Button>
            </Tooltip>
          </div>
        </div>

        {loading ? (
          <LoadingState />
        ) : (
          <AnalysisTable
            gridRef={gridRef}
            selectRmk={selectRmk}
            onGridReady={onGridReady}
            rowData={rowData}
            setRowData={setRowData}
            saveColumnState={saveColumnState}
            scopeFilter={scopeFilter}
          />
        )}
      </Container>
    </React.Fragment>
  );
};

export default React.memo(MainPage);
